<template>
  <div class="google-attachment-component">
    <figure class="google-attachment media" :data-type="dataTypeLocal" :data-media-type="mediaType">
      <inline-loader v-if="isLoading && (!isEmbedType || !isProcessing)" />
      <div
        v-if="isEmbedType && (isProcessing || hasErrorUponRetrieval)"
        class="media-preview status-message"
      >
        <empty-placeholder class="info">
          <template #icon>
            <EmptyImage />
          </template>
          {{ statusMessage }}
        </empty-placeholder>
      </div>

      <div v-if="isEmbedType && !isAnyButtonDisabled && !hasErrorUponRetrieval && !isLoading">
        <div class="iframe-wrapper">
          <iframe :src="iframeLink" height="480" />
        </div>
        <div class="external-source-banner">
          <span class="icon"><svg-icon name="Copyright24" kind="action" /></span>
          <span :data-text="copyRightTip" />
        </div>
      </div>
      <attachment-placeholder
        v-if="!hasErrorUponRetrieval"
        :media-title="mediaTitle"
        :media-type="mediaType"
        :is-inline="isInline"
        :icon-type="iconType"
        icon-kind="others"
        @click="handleAttachmentClicked"
      >
        <template v-if="!hasErrorUponRetrieval" #action-tools>
          <button v-if="!isEmbedType && (isLoading || actionLoading)" class="loader">
            <inline-loader />
          </button>
          <template v-else>
            <button
              v-if="showOpenInNewTab && showButtons"
              class="open-in-new-tab"
              :disabled="disableOpenInNewTab"
              @click.prevent="handleOpenInNewTab"
            >
              <svg-icon :name="openInNewTabIcon" kind="action" />
            </button>
            <button v-if="showTrash && showButtons" class="trash" @click.prevent="handleRemove">
              <svg-icon name="Trash24" kind="action" />
            </button>
            <button
              v-if="showDownload && showButtons"
              class="download"
              :disabled="disableDownload"
              @click.prevent="handleDownload"
            >
              <svg-icon name="Download24" kind="action" />
            </button>

            <cv-interactive-tooltip
              v-if="showCopyRight"
              class="info-tooltip"
              alignment="end"
              direction="bottom"
            >
              <template #trigger>
                <svg-icon name="Info24" kind="action" />
              </template>
              <template #content>
                <p>{{ copyRightTip }}</p>
              </template>
            </cv-interactive-tooltip>
            <overflow-menu
              v-if="showOverflowMenu"
              :flip-menu="true"
              class="post-actions icon-button"
            >
              <template #trigger>
                <svg-icon name="Kebab24" kind="action" />
              </template>
              <!-- Mark As PDF -->
              <OverflowMenuItem v-if="showMarkAsPdf" text-normal @click="handleMarkAsPdf">
                <svg-icon name="MarkAsPDF24" kind="action" />
                <span>{{ t("google_attachment_mark_as_pdf_label") }}</span>
              </OverflowMenuItem>
              <!-- Mark As Google File -->
              <OverflowMenuItem v-if="showMarkAsGoogle" text-normal @click="handleMarkAsGoogleFile">
                <svg-icon name="MarkAsGoogle24" kind="action" />
                <span>{{ t("google_attachment_mark_as_google_file_label") }}</span>
              </OverflowMenuItem>
              <!-- Edit a Copy -->
              <OverflowMenuItem v-if="showEditACopy" text-normal @click="handleEditCopy">
                <svg-icon name="Reply24" kind="action" />
                <span>{{ t("google_attachment_edit_a_copy_label") }}</span>
              </OverflowMenuItem>
              <!-- Open In New Tab -->
              <OverflowMenuItem text-normal @click="handleOpenInNewTab">
                <svg-icon :name="openInNewTabIcon" kind="action" />
                <span>{{ t("google_attachment_open_in_new_tab_label") }}</span>
              </OverflowMenuItem>
              <!-- Download -->
              <OverflowMenuItem @click="handleDownload">
                <svg-icon name="Download24" kind="action" />
                <span>{{ t("google_attachment_download_label") }}</span>
              </OverflowMenuItem>
            </overflow-menu>
          </template>
        </template>
      </attachment-placeholder>
      <attachment-placeholder
        v-else-if="!isEmbedType"
        kind="msg"
        icon-type="Google32"
        icon-kind="others"
        :error="attachmentRetrievalError"
      >
        <template v-if="isProcessError" #action-tools>
          <button v-if="showTrash" class="trash action" @click.prevent="handleRemove">
            <svg-icon name="Trash24" kind="action" />
          </button>
        </template>
      </attachment-placeholder>
      <attachment-placeholder
        v-else
        :media-title="mediaTitle"
        :media-type="mediaType"
        :is-inline="isInline"
        :icon-type="iconType"
        icon-kind="others"
      >
        <template v-if="isProcessError || hasErrorUponRetrieval" #action-tools>
          <button v-if="showTrash" class="trash action" @click.prevent="handleRemove">
            <svg-icon name="Trash24" kind="action" />
          </button>
        </template>
      </attachment-placeholder>
    </figure>
    <message-component v-if="hasGoogleError" class="error submit-validation-error">
      {{ t("lesson_submit_validation_google_error_label") }}
    </message-component>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import {
  GoogleDocumentCreationApiModel,
  GoogleDocumentMetadataModel,
  GoogleDocumentMetadataParam,
  GoogleDocumentProcessingDataByTeamParam,
  GoogleDocumentProcessingStatusParam,
  GrantUserPermissionParam,
} from "@plugins/google/types.ts";
import { RichTextContext } from "@plugins/richtext/types.ts";
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import {
  ATTEMPT_GOOGLE_DOCUMENT_RESOURCE_TYPE,
  CONFIG,
  CONTEXT,
  DOC_USAGE,
  EVENT_ACTION,
  FILE_DEFAULT_FETCH_INTERVAL,
  FILE_FETCH_INTERVAL,
  FILE_PROCESSING_STATE,
  FILE_TYPE,
  ICON_MAP,
  PICKER_ERROR,
  TITLE_SUFFIX,
} from "@plugins/google/google.constant.ts";
import { isDefined, isEmpty, notDefined } from "@utils/var.check.ts";
import { MEDIA_TYPE } from "@plugins/media/media.constant.ts";
import { userGoogleCredential } from "@plugins/google/user.google.credential.ts";
import { ATTEMPT_STATUS, COMPONENT_TYPE } from "@modules/attempt/service/attempt.constant.ts";
import { GoogleAuthEventBus } from "@plugins/google/google.auth.event.bus.ts";
import { noOp } from "@utils/function.utils.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import GoogleAttachmentApi from "@apis/google.attachment.api.ts";
import { COMMENT_RESOURCE_TYPE } from "@plugins/comment/comment.constant.ts";
import { useRoute } from "vue-router";
import { googleDrive } from "@plugins/google/google.drive.ts";
import { useToast } from "@plugins/toast/composables/toast-instance.ts";
import { DATA_STATE } from "@plugins/modal/modal.constant.ts";
import PollingUtils from "@utils/polling.utils.ts";
import { googleAuth } from "@plugins/google/google.auth.ts";
import { useGoogleStore } from "@plugins/google/use-google-store.ts";
import { useSubmitLessonValidationStore } from "@modules/editor/store/use-submit-lesson-validation-store.ts";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useRouting } from "@plugins/common/composables/routing.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useDirty } from "@/plugins/common/composables/page-dirty";
import WaitModal from "@plugins/modal/components/WaitModal.vue";
import { useGoogleDrive } from "@plugins/google/composables/google-drive.ts";

const EmptyImage = defineAsyncComponent(
  () => import("@images/placeholder/empty-states/EmptyImage.svg?component"),
);

interface Props {
  id: string;
  isInline?: boolean;
  lazyload: boolean;
  context?: RichTextContext;
  questionAttemptContext?: object;
  fileStatus?: string;
  resourceStatus?: string;
  processStatus?: string;
  dataType?: string;
  dataContext?: string;
  config?: object;
  openInNewTabOnCreate?: boolean;
  mode: string; //view or edit
  questionAttemptUuids?: string[]; // used in aggregated student response to mark google file / mark as pdf
  tinyMceType?: string;
  usage?: string;
  showButton?: boolean;
  componentType?: string;
  isComment?: boolean;
  duplicatedByEditingTeamMember?: boolean;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_parentComponent?: object;
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_isDynamicallyMounted?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  context: () => ({}),
  questionAttemptContext: () => ({}),
  fileStatus: "",
  resourceStatus: "",
  processStatus: "",
  dataType: "",
  dataContext: "",
  config: () => ({}),
  questionAttemptUuids: () => [],
  tinyMceType: "",
  usage: "",
  showButton: true,
  componentType: "",
  isComment: false,
  duplicatedByEditingTeamMember: false,
  dynamicMountChildMixin_parentComponent: null,
  dynamicMountChildMixin_isDynamicallyMounted: false,
});

const emit = defineEmits([
  "remove",
  "mediaSettings",
  "markGoogleFile",
  "navigateToQuestion",
  "duplicate",
  "duplicationFromTeamMemberCompleted",
  "googleAttachmentClicked",
]);

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const googleStore = useGoogleStore();
const submitLessonValidationStore = useSubmitLessonValidationStore();
const modal = useModal();
const toast = useToast();
const { passiveApiFailHandler } = useErrorHandler();
const { refreshIntervalId, urlReady, urlReadyAndCloseModal, handleRouteIntervalCheck } =
  useRouting();
const { trackWatch } = useEventBusWatch();
const { clearDirty } = useDirty(ref(""));
const {
  hasGoogleDriveAccess,
  checkIfHasGoogleDriveAccess,
  checkUserLinked,
  openNotLinkedModal,
  showAppAvailabilityErrorModal,
  showAppAvailabilityWarningModal,
  skipAction,
} = useGoogleDrive();

useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const isLoading = ref(true);
const isProcessing = ref(false);
const hasErrorUponRetrieval = ref(false);
const isProcessError = ref(false);
const media = ref<Partial<GoogleDocumentMetadataModel>>({});
const attachmentRetrievalError = ref({
  title: "",
  message: "",
});
const userLinkedEmail = ref("");
const actionLoading = ref(false);
const duplicationProcessStatus = ref("");
const tempDocumentId = ref(0);
const eventAction = ref("");
const dataTypeLocal = ref(props.dataType);

const isStudent = computed(() => store.getters["auth/isStudent"]);
const isTeacher = computed(() => store.getters["auth/isTeacher"]);
const userId = computed(() => store.getters["auth/userId"]);
const getRestrictInteraction = computed(() => store.getters["attempt/getRestrictInteraction"]);
const getStudentByUserId = computed(() => store.getters["attempt/getStudentByUserId"]);

const getFileType = (mimeType) => {
  let type = FILE_TYPE.OTHERS.NAME;
  for (const fileType of Object.values(FILE_TYPE)) {
    if (!isEmpty(mimeType) && mimeType.toString() === fileType.VALUE) {
      type = fileType.NAME;
      break;
    }
  }
  return type;
};
const iconType = computed(() => {
  if (!isEmbedType.value && isLoading.value) return "";
  const fileType = getFileType(media.value.type);
  return ICON_MAP[fileType];
});

const isEmbedType = computed(() => props.dataType === CONFIG.EMBED);

const copyRightTip = computed(() => {
  return isEmpty(userLinkedEmail.value)
    ? t("media_iframe_google_copyright_unlinked_message")
    : t("media_iframe_google_copyright_message", {
        userLinkedEmail: userLinkedEmail.value,
      });
});

const mediaTitle = computed(() => {
  if (!isEmbedType.value && isLoading.value) return "";
  return isEmpty(media.value.title) ? "" : media.value.title;
});

const mediaType = computed(() => {
  // internally in this component should always use MediaType, instead from prop
  return media.value.type ? media.value.type : "";
});

const attachmentType = computed(() => {
  // to differentiate audio vs video vs image vs others (google doc/slide/sheet)
  if (mediaType.value.includes("video/")) {
    return MEDIA_TYPE.VIDEO;
  } else if (mediaType.value.includes("audio/")) {
    return MEDIA_TYPE.AUDIO;
  } else if (mediaType.value.includes("image/")) {
    return MEDIA_TYPE.IMAGE;
  }
  return "OTHERS";
});

const isEmbedded = computed(() => dataTypeLocal.value === CONFIG.EMBED);
const isMedia = computed(() => {
  return (
    attachmentType.value === MEDIA_TYPE.IMAGE ||
    attachmentType.value === MEDIA_TYPE.VIDEO ||
    attachmentType.value === MEDIA_TYPE.AUDIO
  );
});
const iframeLink = computed(() => {
  if (isEmbedded.value || isMedia.value) {
    switch (mediaType.value) {
      case FILE_TYPE.DOCUMENT.VALUE: {
        return (
          media.value?.publishedUrl ??
          `https://docs.google.com/document/d/${media.value.documentId}/pub?embedded=true`
        );
      }
      case FILE_TYPE.SPREADSHEET.VALUE: {
        return (
          media.value?.publishedUrl ??
          `https://docs.google.com/spreadsheets/d/${media.value.documentId}/pubhtml?widget=true&amp;headers=false`
        );
      }
      case FILE_TYPE.PRESENTATION.VALUE: {
        return `https://docs.google.com/presentation/d/${media.value.documentId}/embed`;
      }
      default: {
        if (isMedia.value) {
          return `https://drive.google.com/file/d/${media.value.documentId}/preview`;
        }
        return "";
      }
    }
  }
  return "";
});

const publishedLink = computed(() => {
  if (isEmbedded.value && media.value?.publishedUrl) {
    return media.value?.publishedUrl;
  }
  return "";
});

const isTeacherFeedback = computed(() => {
  return (
    (!isEmpty(props.context.uploadFrom) && props.context.uploadFrom === "comment") ||
    props.tinyMceType === "student_teacher_tinymce_4"
  );
});
const isViewMode = computed(() => {
  // cater for case where teacher student view, mode is in edit even if it should be view
  // cause of disabled rich text editor being used
  if (
    props.context.viewingRole === "TEACHER" &&
    props.context.pageDomain === "attempt" &&
    !isTeacherFeedback.value &&
    props.tinyMceType !== "student_teacher_tinymce_4"
  ) {
    return true;
  }
  return props.mode === "view";
});
const isEditMode = computed(() => {
  // cater for case where teacher student view, mode is in edit even if it should be view
  // cause of disabled rich text editor being used
  if (
    props.context.viewingRole === "TEACHER" &&
    props.context.pageDomain === "attempt" &&
    !isTeacherFeedback.value &&
    props.tinyMceType !== "student_teacher_tinymce_4"
  ) {
    return false;
  }
  return props.mode === "edit";
});
const isInlineValue = computed(() => {
  // cater for case where teacher student view, mode is in edit even if it should be view
  // cause of disabled rich text editor being used
  if (
    props.context.viewingRole === "TEACHER" &&
    props.context.pageDomain === "attempt" &&
    !isTeacherFeedback.value
  )
    return false;
  return props.isInline;
});

const isTextComponent = computed(() => {
  return props.componentType === COMPONENT_TYPE.TEXT;
});
const disableIconsForAttempt = computed(() => {
  // to disable download/open in new tab BEFORE it is duplicated for student attempt when in view mode.
  return (
    isViewMode.value &&
    props.dataContext === CONTEXT.LESSON &&
    props.context.pageDomain === CONTEXT.ATTEMPT &&
    props.context.viewingRole === "STUDENT" &&
    !isTextComponent.value
  );
});
const disableOpenInNewTab = computed(() => {
  if (actionLoading.value) return true;
  if (disableIconsForAttempt.value) return true;

  //enable buttons if user is not linked to google account
  if (isEmpty(userGoogleCredential.getCredential().userLinkedEmail)) {
    return false;
  }

  return !isDefined(media.value.documentId);
});
const disableDownload = computed(() => {
  if (actionLoading.value) return true;
  if (disableIconsForAttempt.value) return true;
  if (isEmpty(userGoogleCredential.getCredential().userLinkedEmail)) {
    return false;
  }

  return !isDefined(media.value.documentId);
});
const isAnyButtonDisabled = computed(() => {
  return disableOpenInNewTab.value || disableDownload.value;
});

const openInNewTabIcon = computed(() => {
  if (isEmbedType.value && props.dataContext !== CONTEXT.NOTEBOOK) {
    return "ExternalLink24";
  }
  if (isViewMode.value) {
    return "ExternalLink24";
  } else {
    if (DOC_USAGE.MARKING === props.usage) {
      return "Pen24";
    } else {
      return "Pencil24";
    }
  }
});
const showOpenInNewTab = computed(() => {
  if (isEmbedType.value && isEmbedded.value) return true;
  return !isLoading.value;
});

const showTrash = computed(() => {
  return (
    (!isEmbedType.value && !isAnyButtonDisabled.value && props.isInline && isEditMode.value) ||
    (isEmbedType.value && isEditMode.value)
  );
});

const isAttemptPending = computed(() => {
  if (!props.questionAttemptContext) {
    return false;
  }

  if (!props.questionAttemptContext.status) {
    return false;
  }

  return props.questionAttemptContext.status.toUpperCase() === ATTEMPT_STATUS.PENDING;
});
const showDownload = computed(() => {
  return (
    !isLoading.value &&
    !isInlineValue.value &&
    !isEmbedded.value &&
    isViewMode.value &&
    ((!isAttemptPending.value && isTeacher.value) || (isAttemptPending.value && isStudent.value))
  );
});

const showCopyRight = computed(() => {
  return !isEmbedded.value && !isLoading.value && props.showButton;
});

const isLessonDataContext = computed(() => {
  return props.dataContext === CONTEXT.LESSON;
});
const isAttemptDataContext = computed(() => {
  return props.dataContext === CONTEXT.ATTEMPT;
});
const createdBy = computed(() => {
  return isEmpty(googleStore.getGoogleDocument(props.dataContext, props.id))
    ? null
    : googleStore.getGoogleDocument(props.dataContext, props.id).createdBy;
});
const isCreator = computed(() => {
  return createdBy.value != null && createdBy.value === parseInt(userId.value);
});
const showOverflowMenu = computed(() => {
  if (isAttemptPending.value) return false;
  if (isLoading.value || isProcessError.value || actionLoading.value) return false;
  if (isAttemptDataContext.value && isViewMode.value && !isTeacherFeedback.value) return true;
  return (
    isAttemptDataContext.value && isViewMode.value && isTeacherFeedback.value && !isCreator.value
  );
});
const showButtons = computed(() => {
  if (isEmbedType.value) return true;
  if (!props.showButton) return false;
  if (isLoading.value || isProcessError.value || actionLoading.value) return false;
  if (isLessonDataContext.value) return true;
  if (isAttemptDataContext.value && isEditMode.value) return true;
  if (isAttemptDataContext.value && isAttemptPending.value) return true;
  return (
    isAttemptDataContext.value && isViewMode.value && isTeacherFeedback.value && isCreator.value
  );
});

const isPdf = computed(() => {
  return mediaType.value === FILE_TYPE.PDF.VALUE;
});
const isOtherTeacherTeacherFeedback = computed(() => {
  // flag used to show edit a copy - viewing another teacher's teacher feedback
  return (
    isTeacher.value && notDefined(getStudentByUserId.value(createdBy.value)) && !isCreator.value
  );
});
const showMarkAsGoogle = computed(() => {
  if (getRestrictInteraction.value) return false;
  if (!isTeacher.value) return false;
  if (isOtherTeacherTeacherFeedback.value) return false;
  if (isPdf.value) return false;
  return true;
});
const showMarkAsPdf = computed(() => {
  if (getRestrictInteraction.value) return false;
  if (!isTeacher.value) return false;
  if (isOtherTeacherTeacherFeedback.value) return false;
  if (isTeacherFeedback.value && !isPdf.value) return false;
  return true;
});
const showEditACopy = computed(() => {
  if (getRestrictInteraction.value) return false;
  if (!isTeacherFeedback.value || isCreator.value) return false;
  return isStudent.value || isOtherTeacherTeacherFeedback.value;
});

const validationErrors = computed(() => {
  if (!submitLessonValidationStore) return {};
  return Object.assign({}, submitLessonValidationStore.getSubmissionValidationErrors());
});
const hasGoogleError = computed(() => {
  if (validationErrors.value) {
    const googleDocIds = validationErrors.value["googleDocument"];
    return !isEmpty(googleDocIds) && googleDocIds.split(",").indexOf(props.id) > -1;
  }
  return false;
});

const statusMessage = computed(() => {
  if (hasErrorUponRetrieval.value) {
    return t("embedded_file_fail_message");
  }
  return t("embedded_file_processing_message");
});

const getAttemptGoogleDocumentResourceType = (resourceType) => {
  if (
    resourceType === COMMENT_RESOURCE_TYPE.ATTEMPT_HISTORY ||
    resourceType === COMMENT_RESOURCE_TYPE.TEAM_ATTEMPT_HISTORY
  ) {
    return ATTEMPT_GOOGLE_DOCUMENT_RESOURCE_TYPE.ATTEMPT_HISTORY;
  } else {
    return ATTEMPT_GOOGLE_DOCUMENT_RESOURCE_TYPE.QUESTION_ATTEMPT;
  }
};

const failedPermissionErrorHandling = () => {
  actionLoading.value = false;
  hasErrorUponRetrieval.value = true;
  attachmentRetrievalError.value.title = t("google_attachment_deleted_error_msg");
  throw new Error("Error when grant user permission to google attachment");
};
const grantUserPermission = async (documentId) => {
  const requestData: GrantUserPermissionParam = {
    lessonUuid: props.context.lessonUuid,
    fileId: documentId,
  };

  if (props.context.isStarredResource) {
    const starredResourceLessonUuid = props.context.getStarredResourceLessonUuid();

    if (starredResourceLessonUuid) {
      requestData.lessonUuid = starredResourceLessonUuid;
    }
  }

  if (!isEmpty(route.query.assignmentUuid)) {
    requestData.assignmentUuid = route.query.assignmentUuid;
  } else if (!isEmpty(props.context.assignmentUuid)) {
    requestData.assignmentUuid = props.context.assignmentUuid;
  }

  if (isDefined(props.resourceStatus) && !isEmpty(props.resourceStatus)) {
    requestData.resourceStatus = props.resourceStatus;
  }
  if (props.dataContext === CONTEXT.ATTEMPT) {
    const context = await props.config.getContext();
    requestData.resourceUuid = context.resourceUuid;
    requestData.resourceType = getAttemptGoogleDocumentResourceType(context.resourceType);
    requestData.academicYear = props.context.academicYear;
    requestData.attemptType = CONTEXT.ATTEMPT.toUpperCase();
    requestData.pageId = props.context.pageId;
    requestData.isTeacherFeedback = isTeacherFeedback.value;
    requestData.isCreator = isCreator.value || context.commentedByUserId === parseInt(userId.value);
    if (isTeacherFeedback.value) {
      // mode is either view or edit
      requestData.mode = isViewMode.value ? "view" : "edit";
    }

    const grantPermissionResponse =
      await GoogleAttachmentApi.grantUserPermissionInStudentAttempt(requestData);

    if (grantPermissionResponse.code === "error" || grantPermissionResponse.data === false) {
      failedPermissionErrorHandling();
    }
  } else if (props.dataContext === CONTEXT.LESSON) {
    const grantPermissionResponse = await GoogleAttachmentApi.grantUserPermission(
      requestData,
    ).catch(() => {
      failedPermissionErrorHandling();
    });

    if (grantPermissionResponse.code === "error" || grantPermissionResponse.data === false) {
      failedPermissionErrorHandling();
    }
  } else if (props.dataContext === CONTEXT.NOTEBOOK) {
    requestData.notebookUuid = props.context.notebookUuid;
    const grantPermissionResponse = await GoogleAttachmentApi.grantUserPermissionInNotebook(
      requestData,
    ).catch(() => {
      failedPermissionErrorHandling();
    });

    if (grantPermissionResponse.code === "error" || grantPermissionResponse.data === false) {
      failedPermissionErrorHandling();
    }
  }
};
const getFileData = async (documentId, retryNo) => {
  let fileNoFound = false;
  await googleDrive
    .getUserDriveFileData(documentId)
    .then((resultData) => {
      if (isDefined(resultData)) {
        isLoading.value = false;
        media.value = { ...media.value, ...resultData };
        media.value["title"] = resultData.name;
      }
    })
    .catch((error) => {
      if (isDefined(error.status) && error.status === 404) {
        fileNoFound = true;
      } else {
        isLoading.value = false;
        actionLoading.value = false;
        if (isDefined(error.message)) {
          showGoogleLinkAccountPopUpErrorMessage(error.message.error);
        } else {
          hasErrorUponRetrieval.value = true;
          attachmentRetrievalError.value.title = t("google_attachment_deleted_error_msg");
          throw new Error(error);
        }
      }
    });

  if (fileNoFound) {
    let interval = FILE_DEFAULT_FETCH_INTERVAL;
    if (retryNo < FILE_FETCH_INTERVAL.length) {
      interval = FILE_FETCH_INTERVAL[retryNo];
    }
    setTimeout(() => {
      getFileData(media.value.documentId, retryNo + 1);
    }, interval);
  }
  return Promise.resolve(true);
};
const showGoogleLinkAccountPopUpErrorMessage = (errorMessage) => {
  switch (errorMessage) {
    case "popup_closed_by_user":
      toast.show("error", {
        kind: "error",
        title: "Popup is closed by user.",
      });
      throw new Error("Popup is closed by user.");
    case "access_denied":
      toast.show("error", {
        kind: "error",
        title: "Access denied.",
      });
      throw new Error("Access denied.");
    default:
      throw new Error("Error occurred.");
  }
};

const waitModalParams = {
  state: "processing",
  stateMessage: {
    processing: {
      title: t("google_long_wait_time_duplicating_resource_title"),
      content: t("google_long_wait_time_duplicating_resource_content"),
    },
    complete: {
      title: t("google_long_wait_time_duplicated_resource_title"),
      content: t("google_long_wait_time_duplicated_resource_content"),
    },
  },
  secondaryButtonText: t("google_back_to_question_label"),
};

const waitModalState = computed(() => {
  if (["processing", "complete", "error"].includes(props.processStatus)) {
    return props.processStatus;
  } else if (props.processStatus === "processed") {
    return "complete";
  }
  return "processing";
});

const modalPrimaryButtonText = computed(() => {
  return waitModalState.value === "complete" ? t("google_view_file_label") : "";
});

const showDuplicateModal = async () => {
  emit("duplicate", {
    documentId: media.value.documentId,
    id: props.id,
    mimeType: media.value.type,
    title: media.value.title,
  });

  modal.show(
    WaitModal,
    {
      ...waitModalParams,
      state: waitModalState.value,
      primaryButtonText: modalPrimaryButtonText.value,
    },
    {
      "action-primary": () => {
        clearDirty();
        handleOpenInNewTabAction();
      },
      "action-secondary": () => {
        clearDirty();
        actionLoading.value = false;
        modal.removeAll();
      },
    },
  );
};
const handleOpenInNewTabAction = async () => {
  try {
    await checkUserLinked();
  } catch (error) {
    if (error === PICKER_ERROR.EMPTY_EMAIL) openNotLinkedModal("open");
    return;
  }

  // only NOTEBOOK embedded need to retrieve file data (as document url change) and grant user permission
  if (!isEmbedded.value || props.dataContext === CONTEXT.NOTEBOOK) {
    await grantUserPermission(media.value.documentId);

    // Add timeout to allow Google permission propagation
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }

  if (isEmbedded.value) {
    if (props.dataContext === CONTEXT.NOTEBOOK) {
      urlReadyAndCloseModal.value = true;
      urlReady.value = media.value.documentUrl;
    } else {
      urlReadyAndCloseModal.value = true;
      urlReady.value = publishedLink.value;
    }
  } else {
    actionLoading.value = false;
    urlReadyAndCloseModal.value = true;
    urlReady.value = media.value.documentUrl;
  }
};
const handleOpenInNewTab = () => {
  if ((isLoading.value || actionLoading.value) && skipAction.value) return;

  // embedded doesnt need triple dot loading state
  if (!isEmbedded.value) {
    actionLoading.value = true;
  }
  eventAction.value = EVENT_ACTION.OPEN_IN_NEW_TAB;
  handleRouteIntervalCheck();
  checkUserLinked().then(
    () => {
      if (skipAction.value) {
        clearInterval(refreshIntervalId.value);
        return;
      }
      //  Check for
      //  1) Attachment is from lesson side
      //  2) User is at attempt page
      //  3) ViewingRole is Not Teacher (since Teacher dont need duplicate, cater for Assignment view)
      //  4) Attachment is from rich text editor side (RichText aka view mode dont need duplicate)
      if (
        props.dataContext === CONTEXT.LESSON &&
        props.context.pageDomain === CONTEXT.ATTEMPT &&
        props.context.viewingRole !== "TEACHER" &&
        props.mode === "edit"
      ) {
        checkIfHasGoogleDriveAccess(showAppAvailabilityErrorModal, showDuplicateModal, noOp);
      } else {
        checkIfHasGoogleDriveAccess(
          showAppAvailabilityErrorModal,
          hasGoogleDriveAccess.value ? handleOpenInNewTabAction : modal.removeAll(),
          noOp,
        );
      }
    },
    (error) => {
      isLoading.value = false;
      actionLoading.value = false;
      if (error === PICKER_ERROR.EMPTY_EMAIL) {
        openNotLinkedModal("open");
      }
      clearInterval(refreshIntervalId.value);
    },
  );
};

const handleRemove = (e) => {
  if (isLoading.value && skipAction.value) return;
  const target = e.target;
  const node = e.target.closest("div.google-attachment-wrapper");

  isLoading.value = true;
  eventAction.value = EVENT_ACTION.REMOVE;
  checkUserLinked().then(
    () => {
      if (skipAction.value) return;
      checkIfHasGoogleDriveAccess(
        showAppAvailabilityWarningModal,
        () => {
          isLoading.value = false;
          emit("remove", {
            id: props.id,
            target: target,
            targetNode: node,
          });
        },
        () => {
          isLoading.value = false;
        },
      );
    },
    (error) => {
      isLoading.value = false;
      if (error === PICKER_ERROR.EMPTY_EMAIL) {
        openNotLinkedModal("remove");
      }
    },
  );
};

const performDownload = async () => {
  await grantUserPermission(media.value.documentId);
  await getFileData(media.value.documentId, 0);
  let link = "";
  switch (mediaType.value) {
    case FILE_TYPE.DOCUMENT.VALUE:
      link =
        media.value.exportLinks[
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];
      break;
    case FILE_TYPE.SPREADSHEET.VALUE:
      link =
        media.value.exportLinks[
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ];
      break;
    case FILE_TYPE.PRESENTATION.VALUE:
      link =
        media.value.exportLinks[
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        ];
      break;

    default:
      if (isMedia.value) {
        link = `https://drive.google.com/uc?id=${media.value.documentId}&export=download&authuser=${userLinkedEmail.value}`;
      } else {
        link = media.value.webContentLink;
      }
  }
  isLoading.value = false;
  actionLoading.value = false;
  urlReady.value = link;
};
const performDriveDownload = async () => {
  await grantUserPermission(media.value.documentId);
  let link = "";
  switch (mediaType.value) {
    case FILE_TYPE.DOCUMENT.VALUE:
      link = `https://docs.google.com/document/d/${media.value.documentId}/export?format=doc`;
      break;
    case FILE_TYPE.SPREADSHEET.VALUE:
      link = `https://docs.google.com/spreadsheets/d/${media.value.documentId}/export?format=xlsx`;
      break;
    case FILE_TYPE.PRESENTATION.VALUE:
      link = `https://docs.google.com/presentation/d/${media.value.documentId}/export/pptx`;
      break;
    default:
      link = `https://drive.google.com/uc?id=${media.value.documentId}&export=download&authuser=${userLinkedEmail.value}`;
  }
  isLoading.value = false;
  actionLoading.value = false;
  urlReady.value = link;
};
const handleDownload = () => {
  if ((isLoading.value || actionLoading.value) && skipAction.value) return;

  actionLoading.value = true;
  eventAction.value = EVENT_ACTION.DOWNLOAD;
  handleRouteIntervalCheck();
  checkUserLinked().then(
    () => {
      if (skipAction.value) {
        clearInterval(refreshIntervalId.value);
        return;
      }
      checkIfHasGoogleDriveAccess(
        showAppAvailabilityErrorModal,
        () => {
          if (!props.isComment) {
            performDownload();
          } else {
            performDriveDownload();
          }
        },
        noOp,
      );
    },
    (error) => {
      actionLoading.value = false;
      if (error === PICKER_ERROR.EMPTY_EMAIL) {
        openNotLinkedModal("download");
      }
      clearInterval(refreshIntervalId.value);
    },
  );
};

const checkGoogleDocumentStatus = async () => {
  let checkDocumentApi;
  let params: GoogleDocumentProcessingStatusParam;
  if (props.dataContext === CONTEXT.ATTEMPT) {
    checkDocumentApi = GoogleAttachmentApi.checkDocumentStatusInStudentAttempt;
    const lessonUuid = props.context.printView ? route.params.lessonUuid : props.context.lessonUuid;
    params = {
      googleDocumentId: tempDocumentId.value !== 0 ? tempDocumentId.value : props.id,
      academicYear: props.context.academicYear,
      lessonUuid: lessonUuid,
    };
  } else if (props.dataContext === CONTEXT.LESSON) {
    const lessonUuid = props.context.printView ? route.params.lessonUuid : props.context.lessonUuid;
    checkDocumentApi = GoogleAttachmentApi.checkDocumentStatus;
    params = {
      googleDocumentId: props.id,
      lessonUuid: lessonUuid,
      recordType: CONTEXT.LESSON.toUpperCase(),
    };
  } else if (props.dataContext === CONTEXT.NOTEBOOK) {
    const lessonUuid = props.context.printView
      ? route.params.notebookUuid
      : props.context.notebookUuid;
    checkDocumentApi = GoogleAttachmentApi.checkDocumentStatus;
    params = {
      googleDocumentId: props.id,
      lessonUuid: lessonUuid,
      recordType: CONTEXT.NOTEBOOK.toUpperCase(),
    };
  }

  const continueCheckingForFetchStatus = (response) => {
    return !(response.code === "ok" && !response.data);
  };

  const pollingResponse = await PollingUtils.pollingWithInterval(
    params,
    checkDocumentApi,
    continueCheckingForFetchStatus,
    Date.now(),
    300000,
    5000,
  ).catch((error) => {
    isLoading.value = false;
    actionLoading.value = false;
    if (error.response.status === 404) {
      isProcessError.value = true;
      hasErrorUponRetrieval.value = true;
      attachmentRetrievalError.value.title = t("google_attachment_deleted_error_msg");
    } else {
      isProcessError.value = true;
      attachmentRetrievalError.value.title = t("google_attachment_process_error_msg");
      hasErrorUponRetrieval.value = true;
      passiveApiFailHandler(error);
    }
    return Promise.reject(error);
  });

  if (isDefined(pollingResponse) && pollingResponse.code === "ok" && pollingResponse.data) {
    hasErrorUponRetrieval.value = true;
    isProcessError.value = true;
    attachmentRetrievalError.value.title = t("google_attachment_deleted_error_msg");
    return Promise.resolve(true);
  }

  if (isDefined(pollingResponse) && pollingResponse.code === "ok" && !pollingResponse.data) {
    isLoading.value = false;
    if (duplicationProcessStatus.value === "processing") {
      duplicationProcessStatus.value = "processed";
      duplicationProcessStatus.value = "";
      handleOpenInNewTabAction();
    }
  }
};

const duplicateMarkGoogleAttachment = (exportAsPdf, documentDuplicationRequired) => {
  return new Promise((resolve, reject) => {
    if (!exportAsPdf && !documentDuplicationRequired) {
      return resolve({
        id: props.id,
      });
    }
    const suffix =
      isTeacher.value && !isOtherTeacherTeacherFeedback.value
        ? TITLE_SUFFIX.marked
        : TITLE_SUFFIX.edited;
    const newTitle = media.value.title.replace(/\.[^/.]+$/, "") + " " + suffix;
    const requestData: Partial<GoogleDocumentCreationApiModel> = {
      lessonId: props.context.lessonId,
      sourceDocumentId: media.value.documentId,
      sourceDocumentTitle: newTitle,
      sourceDocumentType: mediaType.value,
      toBePublished: false,
      withinSLS: true,
      exportAsPdf: isPdf.value ? false : exportAsPdf,
    };

    if (props.context.attemptContext) {
      requestData.attemptableUuid = props.context.attemptContext.attemptableUuid;
      requestData.attemptableType = props.context.attemptContext.attemptableType;
    }

    GoogleAttachmentApi.createGoogleAttachmentInStudentAttempt(requestData).then(
      (response) => {
        if (response.code === "error") return reject(response);
        return resolve(response.data);
      },
      (error) => reject(error),
    );
  });
};
const failedDuplicateMarkGoogleAttachment = () => {
  modal.removeAll();
  passiveApiFailHandler();
};
const markGoogleDocument = async (exportAsPdf, documentDuplicationRequired, usage) => {
  duplicationProcessStatus.value = "processing";
  duplicateMarkGoogleAttachment(exportAsPdf, documentDuplicationRequired).then((response) => {
    tempDocumentId.value = response.id;
    checkGoogleDocumentStatus();
    emit("markGoogleFile", { id: response.id, usage: usage });
    isLoading.value = false;
    actionLoading.value = false;
    modal.removeAll();
    nextTick(() => {
      emit("navigateToQuestion", tempDocumentId.value);
      tempDocumentId.value = 0;
      duplicationProcessStatus.value = "";
    });
  }, failedDuplicateMarkGoogleAttachment);
};
const handleMarkGoogleDocument = (
  exportAsPdf,
  documentDuplicationRequired,
  usage,
  modalInstance,
) => {
  if ((isLoading.value || actionLoading.value) && skipAction.value) return;

  if (modalInstance) {
    modalInstance.setDataState(DATA_STATE.LOADING);
  }
  isLoading.value = true;
  actionLoading.value = true;
  eventAction.value = exportAsPdf ? EVENT_ACTION.MARK_AS_PDF : EVENT_ACTION.MARK_AS_GOOGLE_FILE;
  checkUserLinked().then(
    () => {
      if (skipAction.value) return;
      checkIfHasGoogleDriveAccess(
        showAppAvailabilityErrorModal,
        () => {
          if (hasGoogleDriveAccess.value) {
            markGoogleDocument(exportAsPdf, documentDuplicationRequired, usage);
          } else {
            modal.removeAll();
          }
        },
        () => {
          if (modalInstance) {
            modalInstance.setDataState(DATA_STATE.DEFAULT);
          }
          isLoading.value = false;
          actionLoading.value = false;
        },
      );
    },
    (error) => {
      if (modalInstance) {
        modalInstance.setDataState(DATA_STATE.ERROR);
      }
      isLoading.value = false;
      actionLoading.value = false;
      if (error === PICKER_ERROR.EMPTY_EMAIL) openNotLinkedModal("open");
    },
  );
};
const handleEditCopy = () => {
  handleMarkGoogleDocument(false, true, DOC_USAGE.EDITING);
};

const handleMarkAsPdf = () => {
  if ((isLoading.value || actionLoading.value) && skipAction.value) return;
  checkUserLinked().then(
    () => {
      if (skipAction.value) return;
      checkIfHasGoogleDriveAccess(showAppAvailabilityErrorModal, () => {
        const props = {
          title: t("google_attachment_mark_as_pdf_title"),
          content: t("google_attachment_mark_as_pdf_content"),
          useStateButton: true,
          primaryBtnCompletedLabel: t("button_state_deleted"),
          primaryBtnErrorLabel: t("button_state_error"),
          primaryBtnLabel: t("button_ok"),
          secondaryBtnLabel: t("button_cancel"),
          actionPrimary: (modalInstance) => {
            modalInstance.setDataState(DATA_STATE.LOADING);
            markGoogleDocument(true, false, DOC_USAGE.MARKING);
          },
        };
        if (hasGoogleDriveAccess.value) {
          modal.showMessage("warning", props);
        } else {
          modal.removeAll();
        }
      });
    },
    (error) => {
      if (error === PICKER_ERROR.EMPTY_EMAIL) {
        modal.removeAll();
        openNotLinkedModal("pdfMark");
      }
    },
  );
};
const handleMarkAsGoogleFile = () => {
  if ((isLoading.value || actionLoading.value) && skipAction.value) return;
  checkUserLinked().then(
    () => {
      if (skipAction.value) return;
      checkIfHasGoogleDriveAccess(showAppAvailabilityErrorModal, () => {
        const props = {
          title: t("google_attachment_mark_as_google_title"),
          content: t("google_attachment_mark_as_google_content"),
          useStateButton: true,
          primaryBtnCompletedLabel: t("button_state_deleted"),
          primaryBtnErrorLabel: t("button_state_error"),
          primaryBtnLabel: t("button_ok"),
          secondaryBtnLabel: t("button_cancel"),
          actionPrimary: (modalInstance) => {
            modalInstance.setDataState(DATA_STATE.LOADING);
            markGoogleDocument(false, true, DOC_USAGE.MARKING);
          },
        };
        if (hasGoogleDriveAccess.value) {
          modal.showMessage("warning", props);
        } else {
          modal.removeAll();
        }
      });
    },
    (error) => {
      if (error === PICKER_ERROR.EMPTY_EMAIL) {
        modal.removeAll();
        openNotLinkedModal("googleMark");
      }
    },
  );
};

const handleAttachmentClicked = async (e) => {
  e.stopImmediatePropagation();
  if (
    !isStudent.value ||
    isEmpty(props.context.activityTeamId) ||
    props.dataContext !== CONTEXT.LESSON
  ) {
    return;
  }

  // For editing team members
  if (isEditMode.value) {
    // check google account linked before proceeding
    try {
      await checkUserLinked();
    } catch (error) {
      if (error === PICKER_ERROR.EMPTY_EMAIL) {
        modal.removeAll();
        openNotLinkedModal("open");
      }
      return;
    }

    googleStore.setSourceDocumentIdInModal(media.value.documentId);

    modal.show(
      WaitModal,
      {
        ...waitModalParams,
        state: waitModalState.value,
        primaryButtonText: modalPrimaryButtonText.value,
      },
      {
        "action-primary": () => {
          clearDirty();
          handleOpenInNewTabAction();
        },
        "action-secondary": () => {
          clearDirty();
          googleStore.setSourceDocumentIdInModal("");
          modal.removeAll();
        },
      },
    );
    return;
  }

  // For non-editing team members
  if (isViewMode.value) {
    emit("googleAttachmentClicked", {
      handlePollingForNonEditingTeamMembers: handlePollingForNonEditingTeamMembers,
    });
  }
};

const handlePollingForNonEditingTeamMembers = async () => {
  actionLoading.value = true;
  const continueCheckingForFetchStatus = (response) => {
    if (response.code !== "ok") {
      return false;
    }

    return !(
      response.code === "ok" &&
      !isEmpty(response.data) &&
      response.data.status === FILE_PROCESSING_STATE.READY
    );
  };

  modal.show(WaitModal, waitModalParams, {
    "action-secondary": () => {
      clearDirty();
      modal.removeAll();
    },
  });

  const params: GoogleDocumentProcessingDataByTeamParam = {
    teamId: props.context.activityTeamId,
    academicYear: props.context.academicYear,
    sourceDocumentId: media.value.documentId,
  };

  const pollingResponse = await PollingUtils.pollingWithInterval(
    params,
    GoogleAttachmentApi.getDocumentProcessingDataInStudentAttemptByTeam,
    continueCheckingForFetchStatus,
    Date.now(),
    300000,
    5000,
  ).catch((error) => {
    actionLoading.value = false;
    passiveApiFailHandler(error);
  });

  if (
    isDefined(pollingResponse) &&
    pollingResponse.code === "ok" &&
    !isEmpty(pollingResponse.data)
  ) {
    emit("duplicationFromTeamMemberCompleted", {
      oldDocumentId: props.id,
      newDocumentMetadata: pollingResponse.data,
    });
    actionLoading.value = false;
  } else {
    modal.removeAll();
    actionLoading.value = false;
    passiveApiFailHandler();
  }
};

const handleCallback = () => {
  // if email not in sync
  if (googleAuth.requireSignIn) {
    googleAuth.handleRequestCode();
    return;
  }

  // process event action
  if (isEmpty(eventAction.value)) return;
  skipAction.value = false;
  switch (eventAction.value) {
    case EVENT_ACTION.OPEN_IN_NEW_TAB:
      handleOpenInNewTab();
      break;
    case EVENT_ACTION.DOWNLOAD:
      handleDownload();
      break;
    case EVENT_ACTION.MARK_AS_PDF:
      handleMarkAsPdf();
      break;
    case EVENT_ACTION.MARK_AS_GOOGLE_FILE:
      handleMarkAsGoogleFile();
      break;
    case EVENT_ACTION.REMOVE:
      handleRemove();
      break;
    default:
      break;
  }
};

const getAttachmentInfo = async () => {
  // TODO: [SLSSIT-15256] printview context.lessonUuid is undefined.
  // print.view.mixin - provide() executed first, store value undefined.
  // should not modified props directly. Replaced the code by added new local const.
  const lessonUuid = props.context.printView ? route.params.lessonUuid : props.context.lessonUuid;

  const requestData: GoogleDocumentMetadataParam = {
    lessonUuid: lessonUuid,
    googleDocumentId: props.id,
    academicYear: props.context.academicYear,
    notebookUuid: props.context.notebookUuid,
  };

  if (props.context.isStarredResource) {
    const starredResourceLessonUuid = props.context.getStarredResourceLessonUuid();

    if (starredResourceLessonUuid) {
      requestData.lessonUuid = starredResourceLessonUuid;
    }
  }

  if (isDefined(props.resourceStatus) && !isEmpty(props.resourceStatus)) {
    requestData.resourceStatus = props.resourceStatus;
  }
  if (props.dataContext === CONTEXT.ATTEMPT) {
    return new Promise((resolve) => {
      GoogleAttachmentApi.getAttachmentMetaInStudentAttempt(requestData).then((response) => {
        const attachmentMeta = response.data;
        isProcessing.value = attachmentMeta.status === FILE_PROCESSING_STATE.PROCESSING;
        if (attachmentMeta.status === FILE_PROCESSING_STATE.PROCESSING) {
          isLoading.value = true;
        } else {
          isLoading.value = false;
        }
        media.value = attachmentMeta;
        if (attachmentMeta.status === FILE_PROCESSING_STATE.ERROR) {
          attachmentRetrievalError.value.title = t("google_attachment_process_error_msg");
          hasErrorUponRetrieval.value = true;
          throw new Error("google documentId is required");
        } else {
          if (attachmentMeta.published) {
            dataTypeLocal.value = CONFIG.EMBED;
          } else {
            dataTypeLocal.value = CONFIG.ADD;
          }
          const googleDocumentStoreData = {
            title: attachmentMeta.title,
            mimeType: attachmentMeta.type,
            sourceDocumentId: attachmentMeta.documentId,
            status: attachmentMeta.status,
            createdBy: attachmentMeta.createdBy,
          };
          googleStore.setGoogleDocument(
            CONTEXT.ATTEMPT,
            attachmentMeta.id,
            googleDocumentStoreData,
          );
          resolve();
        }
      }, passiveApiFailHandler);
    });
  } else if (props.dataContext == CONTEXT.LESSON) {
    return new Promise((resolve) => {
      GoogleAttachmentApi.getAttachmentMeta(requestData).then((response) => {
        const attachmentMeta = response.data;
        media.value = attachmentMeta;
        isProcessing.value = attachmentMeta.status === FILE_PROCESSING_STATE.PROCESSING;
        if (attachmentMeta.status === FILE_PROCESSING_STATE.ERROR) {
          isLoading.value = false;
          attachmentRetrievalError.value.title = t("google_attachment_process_error_msg");
          hasErrorUponRetrieval.value = true;
          throw new Error("google documentId is required");
        } else {
          if (attachmentMeta.status === FILE_PROCESSING_STATE.PROCESSING) {
            isLoading.value = true;
          } else {
            isLoading.value = false;
          }
          if (attachmentMeta.published) {
            dataTypeLocal.value = CONFIG.EMBED;
          } else {
            dataTypeLocal.value = CONFIG.ADD;
          }
          const googleDocumentStoreData = {
            title: attachmentMeta.title,
            mimeType: attachmentMeta.type,
            sourceDocumentId: attachmentMeta.documentId,
            status: attachmentMeta.status,
            createdBy: attachmentMeta.createdBy,
          };
          googleStore.setGoogleDocument(CONTEXT.LESSON, attachmentMeta.id, googleDocumentStoreData);
          resolve();
        }
      }, passiveApiFailHandler);
    });
  } else if (props.dataContext == CONTEXT.NOTEBOOK) {
    return new Promise((resolve) => {
      GoogleAttachmentApi.getAttachmentNotebookMeta(requestData).then((response) => {
        const attachmentMeta = response.data;
        media.value = attachmentMeta;
        isProcessing.value = attachmentMeta.status === FILE_PROCESSING_STATE.PROCESSING;
        if (attachmentMeta.status === FILE_PROCESSING_STATE.ERROR) {
          isLoading.value = false;
          attachmentRetrievalError.value.title = t("google_attachment_process_error_msg");
          hasErrorUponRetrieval.value = true;
          throw new Error("google documentId is required");
        } else {
          if (attachmentMeta.status === FILE_PROCESSING_STATE.PROCESSING) {
            isLoading.value = true;
          } else {
            isLoading.value = false;
          }
          if (attachmentMeta.published) {
            dataTypeLocal.value = CONFIG.EMBED;
          } else {
            dataTypeLocal.value = CONFIG.ADD;
          }
          const googleDocumentStoreData = {
            title: attachmentMeta.title,
            mimeType: attachmentMeta.type,
            sourceDocumentId: attachmentMeta.documentId,
            status: attachmentMeta.status,
            createdBy: attachmentMeta.createdBy,
          };
          googleStore.setGoogleDocument(CONTEXT.LESSON, attachmentMeta.id, googleDocumentStoreData);
          resolve();
        }
      }, passiveApiFailHandler);
    });
  }
};

onMounted(() => {
  trackWatch(GoogleAuthEventBus.$on("handleCallback", handleCallback));
  if (props.dataContext === CONTEXT.ATTEMPT) googleStore.addProcessingGoogleFile(props.id);
  handleRouteIntervalCheck();
  userGoogleCredential.fetchLinkedAccountCredential().then(() => {
    userLinkedEmail.value = userGoogleCredential.getCredential().userLinkedEmail;
    getAttachmentInfo().then(() => {
      checkGoogleDocumentStatus().then(() => {
        getAttachmentInfo().then(() => {
          if (props.dataContext === CONTEXT.ATTEMPT)
            googleStore.removeProcessingGoogleFile(props.id);
          if (!isTeacherFeedback.value) {
            if (props.processStatus !== "processed") {
              return;
            }

            if (
              !props.duplicatedByEditingTeamMember ||
              (googleStore.getSourceDocumentIdInModal() === media.value.sourceDocumentId &&
                media.value.teamId === props.context.activityTeamId)
            ) {
              modal.removeAll();
              googleStore.setSourceDocumentIdInModal("");
              modal.show(
                WaitModal,
                {
                  ...waitModalParams,
                  state: waitModalState.value,
                  primaryButtonText: modalPrimaryButtonText.value,
                },
                {
                  "action-primary": () => {
                    clearDirty();
                    handleOpenInNewTabAction();
                  },
                  "action-secondary": () => {
                    clearDirty();
                    isLoading.value = false;
                    modal.removeAll();
                  },
                },
              );
            }
          }
        });
      });
    });
  });
  if (isEmpty(userLinkedEmail.value)) return;
});

onBeforeUnmount(() => {
  googleStore.removeProcessingGoogleFile(props.id);
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.google-attachment {
  .external-source-banner {
    @include carbon--type-style("label-01", true);
    display: flex;
    align-items: center;
    padding: $spacing-02 0;
    background-color: $inverse-02;
    color: $ui-01;

    .icon {
      display: inline-flex;
    }

    .sls-icon {
      [fill] {
        fill: $ui-01;
      }
    }
  }

  .iframe-wrapper {
    display: flex;
    justify-content: center;
    margin: auto;

    iframe {
      max-width: 100%;

      &:not([width]) {
        flex: 1;
      }
    }

    + .attachment-placeholder {
      border-radius: 0 0 10px 10px;
    }
  }

  .media-preview + {
    .attachment-placeholder {
      border-radius: 0 0 10px 10px;
    }
  }

  .attachment-placeholder {
    min-height: 48px;
    background-color: $ui-01;
    border-radius: 10px;

    button {
      position: relative;

      .inline-loader {
        transform: scale(0.5);
        background-color: transparent;
      }
    }

    .message.message-component {
      flex: 1 1 auto;
      color: $text-02;

      span {
        @include carbon--type-style("body-short-01", true);
      }
    }

    .tools {
      &.has-action-tools-only {
        justify-content: flex-end;
      }

      .button-container {
        display: flex;
        align-items: center;

        ~ .bx--tooltip__trigger {
          margin-left: $spacing-03;
        }
      }

      .info-tooltip {
        .bx--tooltip__trigger {
          margin-left: 0;

          &:hover,
          &:focus {
            svg {
              fill: none;
            }
          }
        }
      }
    }
  }

  .empty-placeholder {
    .view &,
    .lesson-viewer & {
      background-color: $ui-03;
    }
  }

  > .inline-loader {
    border-radius: 10px;
    pointer-events: none;

    + .attachment-placeholder {
      border-radius: 10px;

      .inline-loader {
        display: none;
      }
    }
  }
}
</style>
